'use client';

import { NavArrowLeft, NavArrowRight } from '@byway/icons';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Image } from 'react-datocms/image';
import type { CardGalleryProps, CardGallerySource } from './ListingCard.types';
import styles from './ListingCardGallery.module.css';

const imageSizes = '(min-width: 768px) 360px, calc(100vw - 72px)';

const isDatoSource = (image: CardGallerySource): image is Extract<CardGallerySource, { data: unknown }> =>
  'data' in image;

const hasValidSrc = (image: CardGallerySource) => (isDatoSource(image) ? Boolean(image.data?.src) : Boolean(image.src));

const sourceKey = (image: CardGallerySource, index: number) =>
  `${isDatoSource(image) ? image.data.src : image.src}-${index}`;

const ListingCardGallery = ({ images = [], tag, actions, ariaLabel }: CardGalleryProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const t = useTranslations('aria');

  const sources = images.filter(hasValidSrc);
  const isCarousel = sources.length > 1;
  const hasOverlayTop = tag != null || actions != null;

  if (sources.length === 0) return null;
  const firstSource = sources[0];

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    setCurrentSlide(Math.round(track.scrollLeft / track.clientWidth));
  };

  const scrollToSlide = (index: number) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
  };

  const stepSlide = (delta: number) => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const current = Math.round(track.scrollLeft / track.clientWidth);
    scrollToSlide((current + delta + sources.length) % sources.length);
  };

  return (
    <div data-card-gallery data-test-id="CardGallery" className={styles.gallery}>
      {isCarousel ? (
        <section
          className={styles.carousel}
          aria-roledescription={t('carousel')}
          aria-label={ariaLabel ?? t('carousel')}
        >
          <div ref={trackRef} className={styles.scrollTrack} onScroll={handleScroll}>
            {sources.map((image, index) => (
              <div key={sourceKey(image, index)} className={styles.slide}>
                {isDatoSource(image) ? (
                  <Image data={image.data} layout="fill" objectFit="cover" sizes={imageSizes} />
                ) : (
                  <img
                    src={image.src}
                    alt={image.alt}
                    className={styles.slideImage}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                )}
              </div>
            ))}
          </div>
        </section>
      ) : isDatoSource(firstSource) ? (
        <Image data={firstSource.data} layout="fill" objectFit="cover" sizes={imageSizes} />
      ) : (
        <img src={firstSource.src} alt={firstSource.alt} className={styles.singleImage} loading="lazy" />
      )}

      {isCarousel && <div className={styles.gradientOverlay} data-card-gallery-shade aria-hidden="true" />}

      <div className={styles.overlay}>
        {hasOverlayTop && (
          <div className={styles.overlayTop}>
            <div className={styles.overlayTopLeft}>{tag}</div>
            <div className={styles.overlayTopRight}>{actions}</div>
          </div>
        )}

        {isCarousel && (
          <div className={styles.overlayMiddle}>
            <button
              type="button"
              className={styles.arrowButton}
              onClick={() => stepSlide(-1)}
              aria-label={t('card_gallery_prev_image')}
            >
              <NavArrowLeft decorative size="large" />
            </button>
            <button
              type="button"
              className={styles.arrowButton}
              onClick={() => stepSlide(1)}
              aria-label={t('card_gallery_next_image')}
            >
              <NavArrowRight decorative size="large" />
            </button>
          </div>
        )}

        {isCarousel && (
          <div className={styles.overlayBottom} role="tablist" aria-label={t('card_gallery_slides')}>
            {sources.map((image, index) => (
              <button
                key={`dot-${sourceKey(image, index)}`}
                type="button"
                className={clsx(styles.dot, index === currentSlide && styles.dotActive)}
                onClick={() => scrollToSlide(index)}
                role="tab"
                aria-selected={index === currentSlide}
                aria-label={t('card_gallery_go_to_slide', { slide: index + 1 })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { ListingCardGallery };
